/* ⚠️ GENERATED from app/services/postCompose.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   ✍️ postCompose.ts — the composer's own vocabulary: how a person is feeling or
   what they are doing, the words of the full-screen composer, and what a saved
   draft may hold.

   Shared by the phone and the browser (twin generated by tools/gen-twins.mjs),
   so a post written on one reads the same on the other.

   ── FEELINGS AND ACTIVITIES ────────────────────────────────────────────────
   A post may carry ONE of them, written on the post as
       feeling: { kind: 'feeling',  key: 'happy' }
       feeling: { kind: 'activity', key: 'watching', text: 'the final' }
   🟥 ONLY KEYS ARE STORED, NEVER WORDS OR EMOJI. The words are drawn by the
      reader in the reader's language, so an Arabic reader sees Arabic under an
      English author's post — and a post cannot smuggle its own emoji or label
      into somebody else's feed: `cleanFeeling` runs on READ as well as on
      write, and a key that is not in the catalogue draws nothing.
   🟢 AN ACTIVITY NEEDS ITS OBJECT ("watching …" what?). The object is the one
      piece of free text, trimmed to ACTIVITY_TEXT_MAX and shown as typed.

   ── THE LINE IN THE BYLINE ─────────────────────────────────────────────────
     "Sara is 😊 feeling happy at 📍 Riyadh with Ali"
   `feelingSegment` is the " is 😊 feeling happy" part. When a post has a
   feeling AND a place, the place loses its own "is" (`placeAfterFeeling`), or
   the line would read "is feeling happy is at".

   ── DRAFTS ─────────────────────────────────────────────────────────────────
   Closing the composer with something in it asks "Save as draft / Discard".
   A saved draft is ONE per account, kept on that device (the phone in
   AsyncStorage, the browser in localStorage + IndexedDB for the files), and
   it comes back the next time the composer opens. `cleanDraft` is the shape
   both accept — anything else in storage is ignored rather than trusted.
   ═══════════════════════════════════════════════════════════════════════════ */

export const ACTIVITY_TEXT_MAX = 60;
export const DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
export const DRAFT_MEDIA_MAX = 10;
export const DRAFT_TEXT_MAX = 5000;
const lang2 = lang => String(lang || 'en').slice(0, 2).toLowerCase();
const pick = (row, lang, fallback = '') => row ? row[lang2(lang)] || row.en || fallback : fallback;
const fill = (s, vars) => String(s).replace(/\{(\w+)\}/g, (_m, k) => vars[k] === undefined ? '' : String(vars[k]));

/* ═══ FEELINGS — emoji + the word, ten languages ════════════════════════════ */

export const FEELINGS = [{
  key: 'happy',
  emoji: '😊',
  L: {
    en: 'happy',
    ar: 'سعيد',
    es: 'feliz',
    fr: 'heureux',
    de: 'glücklich',
    pt: 'feliz',
    ru: 'счастливое',
    hi: 'खुश',
    ja: '幸せな',
    zh: '开心'
  }
}, {
  key: 'blessed',
  emoji: '😇',
  L: {
    en: 'blessed',
    ar: 'محظوظ',
    es: 'bendecido',
    fr: 'béni',
    de: 'gesegnet',
    pt: 'abençoado',
    ru: 'благословенное',
    hi: 'धन्य',
    ja: '恵まれた',
    zh: '幸运'
  }
}, {
  key: 'loved',
  emoji: '🥰',
  L: {
    en: 'loved',
    ar: 'محبوب',
    es: 'querido',
    fr: 'aimé',
    de: 'geliebt',
    pt: 'amado',
    ru: 'любимое',
    hi: 'प्यार भरा',
    ja: '愛されている',
    zh: '被爱'
  }
}, {
  key: 'sad',
  emoji: '😢',
  L: {
    en: 'sad',
    ar: 'حزين',
    es: 'triste',
    fr: 'triste',
    de: 'traurig',
    pt: 'triste',
    ru: 'грустное',
    hi: 'उदास',
    ja: '悲しい',
    zh: '难过'
  }
}, {
  key: 'lovely',
  emoji: '☺️',
  L: {
    en: 'lovely',
    ar: 'لطيف',
    es: 'encantador',
    fr: 'charmant',
    de: 'wunderbar',
    pt: 'encantado',
    ru: 'милое',
    hi: 'प्यारा',
    ja: 'すてきな',
    zh: '可爱'
  }
}, {
  key: 'thankful',
  emoji: '🙏',
  L: {
    en: 'thankful',
    ar: 'ممتن',
    es: 'agradecido',
    fr: 'reconnaissant',
    de: 'dankbar',
    pt: 'agradecido',
    ru: 'благодарное',
    hi: 'आभारी',
    ja: '感謝の',
    zh: '感恩'
  }
}, {
  key: 'excited',
  emoji: '🤩',
  L: {
    en: 'excited',
    ar: 'متحمس',
    es: 'emocionado',
    fr: 'excité',
    de: 'aufgeregt',
    pt: 'animado',
    ru: 'восторженное',
    hi: 'उत्साहित',
    ja: 'わくわくした',
    zh: '兴奋'
  }
}, {
  key: 'in_love',
  emoji: '😍',
  L: {
    en: 'in love',
    ar: 'واقع في الحب',
    es: 'enamorado',
    fr: 'amoureux',
    de: 'verliebt',
    pt: 'apaixonado',
    ru: 'влюблённое',
    hi: 'प्यार में',
    ja: '恋している',
    zh: '恋爱中'
  }
}, {
  key: 'crazy',
  emoji: '🤪',
  L: {
    en: 'crazy',
    ar: 'مجنون',
    es: 'loco',
    fr: 'fou',
    de: 'verrückt',
    pt: 'louco',
    ru: 'безумное',
    hi: 'पागल',
    ja: 'クレイジーな',
    zh: '疯狂'
  }
}, {
  key: 'grateful',
  emoji: '🤗',
  L: {
    en: 'grateful',
    ar: 'شاكر',
    es: 'agradecido de corazón',
    fr: 'plein de gratitude',
    de: 'erkenntlich',
    pt: 'grato',
    ru: 'признательное',
    hi: 'कृतज्ञ',
    ja: 'ありがたい',
    zh: '感激'
  }
}, {
  key: 'blissful',
  emoji: '😌',
  L: {
    en: 'blissful',
    ar: 'في غاية السعادة',
    es: 'dichoso',
    fr: 'comblé',
    de: 'selig',
    pt: 'radiante',
    ru: 'блаженное',
    hi: 'आनंदित',
    ja: '至福の',
    zh: '幸福'
  }
}, {
  key: 'fantastic',
  emoji: '😁',
  L: {
    en: 'fantastic',
    ar: 'رائع',
    es: 'fantástico',
    fr: 'super bien',
    de: 'fantastisch',
    pt: 'fantástico',
    ru: 'фантастическое',
    hi: 'शानदार',
    ja: '最高の',
    zh: '超棒'
  }
}, {
  key: 'silly',
  emoji: '😜',
  L: {
    en: 'silly',
    ar: 'مرح',
    es: 'tonto',
    fr: 'farceur',
    de: 'albern',
    pt: 'bobo',
    ru: 'дурашливое',
    hi: 'नटखट',
    ja: 'ふざけた',
    zh: '傻乎乎'
  }
}, {
  key: 'festive',
  emoji: '🥳',
  L: {
    en: 'festive',
    ar: 'احتفالي',
    es: 'festivo',
    fr: 'festif',
    de: 'festlich',
    pt: 'festivo',
    ru: 'праздничное',
    hi: 'उत्सव जैसा',
    ja: 'お祭り気分の',
    zh: '喜庆'
  }
}, {
  key: 'wonderful',
  emoji: '🌟',
  L: {
    en: 'wonderful',
    ar: 'رائع جدًا',
    es: 'maravilloso',
    fr: 'merveilleux',
    de: 'großartig',
    pt: 'maravilhoso',
    ru: 'чудесное',
    hi: 'अद्भुत',
    ja: '素晴らしい',
    zh: '美妙'
  }
}, {
  key: 'cool',
  emoji: '😎',
  L: {
    en: 'cool',
    ar: 'رايق',
    es: 'genial',
    fr: 'cool',
    de: 'cool',
    pt: 'legal',
    ru: 'крутое',
    hi: 'कूल',
    ja: 'クールな',
    zh: '酷'
  }
}, {
  key: 'amused',
  emoji: '😆',
  L: {
    en: 'amused',
    ar: 'مستمتع',
    es: 'divertido',
    fr: 'amusé',
    de: 'amüsiert',
    pt: 'divertido',
    ru: 'весёлое',
    hi: 'मनोरंजित',
    ja: '楽しい',
    zh: '好笑'
  }
}, {
  key: 'relaxed',
  emoji: '😌',
  L: {
    en: 'relaxed',
    ar: 'مسترخٍ',
    es: 'relajado',
    fr: 'détendu',
    de: 'entspannt',
    pt: 'relaxado',
    ru: 'расслабленное',
    hi: 'आराम में',
    ja: 'リラックスした',
    zh: '放松'
  }
}, {
  key: 'positive',
  emoji: '🙂',
  L: {
    en: 'positive',
    ar: 'إيجابي',
    es: 'positivo',
    fr: 'positif',
    de: 'positiv',
    pt: 'positivo',
    ru: 'позитивное',
    hi: 'सकारात्मक',
    ja: '前向きな',
    zh: '积极'
  }
}, {
  key: 'chill',
  emoji: '🧊',
  L: {
    en: 'chill',
    ar: 'هادئ',
    es: 'tranquilo',
    fr: 'zen',
    de: 'locker',
    pt: 'de boa',
    ru: 'спокойное',
    hi: 'शांत',
    ja: 'のんびりした',
    zh: '惬意'
  }
}, {
  key: 'hopeful',
  emoji: '🤞',
  L: {
    en: 'hopeful',
    ar: 'متفائل',
    es: 'esperanzado',
    fr: 'plein d’espoir',
    de: 'hoffnungsvoll',
    pt: 'esperançoso',
    ru: 'обнадёженное',
    hi: 'आशावान',
    ja: '希望に満ちた',
    zh: '充满希望'
  }
}, {
  key: 'joyful',
  emoji: '😄',
  L: {
    en: 'joyful',
    ar: 'مبتهج',
    es: 'alegre',
    fr: 'joyeux',
    de: 'fröhlich',
    pt: 'alegre',
    ru: 'радостное',
    hi: 'आनंदमय',
    ja: 'うれしい',
    zh: '快乐'
  }
}, {
  key: 'proud',
  emoji: '🦁',
  L: {
    en: 'proud',
    ar: 'فخور',
    es: 'orgulloso',
    fr: 'fier',
    de: 'stolz',
    pt: 'orgulhoso',
    ru: 'гордое',
    hi: 'गर्वित',
    ja: '誇らしい',
    zh: '自豪'
  }
}, {
  key: 'motivated',
  emoji: '💪',
  L: {
    en: 'motivated',
    ar: 'متحفز',
    es: 'motivado',
    fr: 'motivé',
    de: 'motiviert',
    pt: 'motivado',
    ru: 'мотивированное',
    hi: 'प्रेरित',
    ja: 'やる気に満ちた',
    zh: '动力十足'
  }
}, {
  key: 'confident',
  emoji: '😏',
  L: {
    en: 'confident',
    ar: 'واثق',
    es: 'seguro',
    fr: 'confiant',
    de: 'selbstbewusst',
    pt: 'confiante',
    ru: 'уверенное',
    hi: 'आत्मविश्वासी',
    ja: '自信に満ちた',
    zh: '自信'
  }
}, {
  key: 'thoughtful',
  emoji: '🤔',
  L: {
    en: 'thoughtful',
    ar: 'متأمل',
    es: 'pensativo',
    fr: 'pensif',
    de: 'nachdenklich',
    pt: 'pensativo',
    ru: 'задумчивое',
    hi: 'विचारशील',
    ja: '考え込んだ',
    zh: '若有所思'
  }
}, {
  key: 'nostalgic',
  emoji: '🥹',
  L: {
    en: 'nostalgic',
    ar: 'حنين',
    es: 'nostálgico',
    fr: 'nostalgique',
    de: 'nostalgisch',
    pt: 'nostálgico',
    ru: 'ностальгическое',
    hi: 'पुरानी यादों में',
    ja: '懐かしい',
    zh: '怀旧'
  }
}, {
  key: 'emotional',
  emoji: '🥲',
  L: {
    en: 'emotional',
    ar: 'متأثر',
    es: 'emocionado hasta las lágrimas',
    fr: 'ému',
    de: 'gerührt',
    pt: 'emocionado',
    ru: 'трогательное',
    hi: 'भावुक',
    ja: '感動した',
    zh: '感动'
  }
}, {
  key: 'tired',
  emoji: '😴',
  L: {
    en: 'tired',
    ar: 'متعب',
    es: 'cansado',
    fr: 'fatigué',
    de: 'müde',
    pt: 'cansado',
    ru: 'усталое',
    hi: 'थका हुआ',
    ja: '疲れた',
    zh: '累'
  }
}, {
  key: 'hungry',
  emoji: '😋',
  L: {
    en: 'hungry',
    ar: 'جائع',
    es: 'hambriento',
    fr: 'affamé',
    de: 'hungrig',
    pt: 'com fome',
    ru: 'голодное',
    hi: 'भूखा',
    ja: 'おなかがすいた',
    zh: '饿'
  }
}, {
  key: 'bored',
  emoji: '😑',
  L: {
    en: 'bored',
    ar: 'ملول',
    es: 'aburrido',
    fr: 'ennuyé',
    de: 'gelangweilt',
    pt: 'entediado',
    ru: 'скучающее',
    hi: 'ऊबा हुआ',
    ja: '退屈な',
    zh: '无聊'
  }
}, {
  key: 'sick',
  emoji: '🤒',
  L: {
    en: 'sick',
    ar: 'مريض',
    es: 'enfermo',
    fr: 'malade',
    de: 'krank',
    pt: 'doente',
    ru: 'больное',
    hi: 'बीमार',
    ja: '具合が悪い',
    zh: '生病'
  }
}, {
  key: 'angry',
  emoji: '😠',
  L: {
    en: 'angry',
    ar: 'غاضب',
    es: 'enojado',
    fr: 'en colère',
    de: 'wütend',
    pt: 'bravo',
    ru: 'сердитое',
    hi: 'गुस्से में',
    ja: '怒った',
    zh: '生气'
  }
}, {
  key: 'alone',
  emoji: '😔',
  L: {
    en: 'alone',
    ar: 'وحيد',
    es: 'solo',
    fr: 'seul',
    de: 'allein',
    pt: 'sozinho',
    ru: 'одинокое',
    hi: 'अकेला',
    ja: 'ひとりぼっちの',
    zh: '孤单'
  }
}, {
  key: 'accomplished',
  emoji: '🏆',
  L: {
    en: 'accomplished',
    ar: 'منجز',
    es: 'realizado',
    fr: 'accompli',
    de: 'erfolgreich',
    pt: 'realizado',
    ru: 'победное',
    hi: 'सफल',
    ja: '達成感のある',
    zh: '有成就感'
  }
}, {
  key: 'ok',
  emoji: '👌',
  L: {
    en: 'OK',
    ar: 'بخير',
    es: 'bien',
    fr: 'bien',
    de: 'okay',
    pt: 'bem',
    ru: 'нормальное',
    hi: 'ठीक',
    ja: 'まあまあの',
    zh: '还行'
  }
}];

/* ═══ ACTIVITIES — the grid label, the prompt for its object, and the line ══ */

export const ACTIVITIES = [{
  key: 'celebrating',
  emoji: '🎉',
  icon: 'party',
  label: {
    en: 'Celebrating…',
    ar: 'يحتفل بـ…',
    es: 'Celebrando…',
    fr: 'Fête…',
    de: 'Feiert…',
    pt: 'Comemorando…',
    ru: 'Празднует…',
    hi: 'जश्न मना रहे हैं…',
    ja: 'お祝い中…',
    zh: '庆祝…'
  },
  ask: {
    en: 'What are you celebrating?',
    ar: 'بماذا تحتفل؟',
    es: '¿Qué estás celebrando?',
    fr: 'Que fêtez-vous ?',
    de: 'Was feierst du?',
    pt: 'O que você está comemorando?',
    ru: 'Что вы празднуете?',
    hi: 'आप किसका जश्न मना रहे हैं?',
    ja: '何をお祝いしていますか？',
    zh: '你在庆祝什么？'
  },
  line: {
    en: ' is {e} celebrating {x}',
    ar: ' {e} يحتفل بـ{x}',
    es: ' {e} está celebrando {x}',
    fr: ' {e} fête {x}',
    de: ' {e} feiert {x}',
    pt: ' {e} está comemorando {x}',
    ru: ' {e} празднует {x}',
    hi: ' {e} {x} का जश्न मना रहे हैं',
    ja: ' {e} {x}をお祝い中',
    zh: ' {e} 正在庆祝{x}'
  }
}, {
  key: 'watching',
  emoji: '📺',
  icon: 'tv',
  label: {
    en: 'Watching…',
    ar: 'يشاهد…',
    es: 'Viendo…',
    fr: 'Regarde…',
    de: 'Schaut…',
    pt: 'Assistindo…',
    ru: 'Смотрит…',
    hi: 'देख रहे हैं…',
    ja: '視聴中…',
    zh: '观看…'
  },
  ask: {
    en: 'What are you watching?',
    ar: 'ماذا تشاهد؟',
    es: '¿Qué estás viendo?',
    fr: 'Que regardez-vous ?',
    de: 'Was schaust du?',
    pt: 'O que você está assistindo?',
    ru: 'Что вы смотрите?',
    hi: 'आप क्या देख रहे हैं?',
    ja: '何を観ていますか？',
    zh: '你在看什么？'
  },
  line: {
    en: ' is {e} watching {x}',
    ar: ' {e} يشاهد {x}',
    es: ' {e} está viendo {x}',
    fr: ' {e} regarde {x}',
    de: ' {e} schaut {x}',
    pt: ' {e} está assistindo {x}',
    ru: ' {e} смотрит {x}',
    hi: ' {e} {x} देख रहे हैं',
    ja: ' {e} {x}を観ています',
    zh: ' {e} 正在看{x}'
  }
}, {
  key: 'eating',
  emoji: '🍔',
  icon: 'food',
  label: {
    en: 'Eating…',
    ar: 'يأكل…',
    es: 'Comiendo…',
    fr: 'Mange…',
    de: 'Isst…',
    pt: 'Comendo…',
    ru: 'Ест…',
    hi: 'खा रहे हैं…',
    ja: '食事中…',
    zh: '吃…'
  },
  ask: {
    en: 'What are you eating?',
    ar: 'ماذا تأكل؟',
    es: '¿Qué estás comiendo?',
    fr: 'Que mangez-vous ?',
    de: 'Was isst du?',
    pt: 'O que você está comendo?',
    ru: 'Что вы едите?',
    hi: 'आप क्या खा रहे हैं?',
    ja: '何を食べていますか？',
    zh: '你在吃什么？'
  },
  line: {
    en: ' is {e} eating {x}',
    ar: ' {e} يأكل {x}',
    es: ' {e} está comiendo {x}',
    fr: ' {e} mange {x}',
    de: ' {e} isst {x}',
    pt: ' {e} está comendo {x}',
    ru: ' {e} ест {x}',
    hi: ' {e} {x} खा रहे हैं',
    ja: ' {e} {x}を食べています',
    zh: ' {e} 正在吃{x}'
  }
}, {
  key: 'drinking',
  emoji: '🥤',
  icon: 'drink',
  label: {
    en: 'Drinking…',
    ar: 'يشرب…',
    es: 'Bebiendo…',
    fr: 'Boit…',
    de: 'Trinkt…',
    pt: 'Bebendo…',
    ru: 'Пьёт…',
    hi: 'पी रहे हैं…',
    ja: '飲んでいる…',
    zh: '喝…'
  },
  ask: {
    en: 'What are you drinking?',
    ar: 'ماذا تشرب؟',
    es: '¿Qué estás bebiendo?',
    fr: 'Que buvez-vous ?',
    de: 'Was trinkst du?',
    pt: 'O que você está bebendo?',
    ru: 'Что вы пьёте?',
    hi: 'आप क्या पी रहे हैं?',
    ja: '何を飲んでいますか？',
    zh: '你在喝什么？'
  },
  line: {
    en: ' is {e} drinking {x}',
    ar: ' {e} يشرب {x}',
    es: ' {e} está bebiendo {x}',
    fr: ' {e} boit {x}',
    de: ' {e} trinkt {x}',
    pt: ' {e} está bebendo {x}',
    ru: ' {e} пьёт {x}',
    hi: ' {e} {x} पी रहे हैं',
    ja: ' {e} {x}を飲んでいます',
    zh: ' {e} 正在喝{x}'
  }
}, {
  key: 'traveling',
  emoji: '✈️',
  icon: 'plane',
  label: {
    en: 'Traveling to…',
    ar: 'مسافر إلى…',
    es: 'Viajando a…',
    fr: 'En route pour…',
    de: 'Reist nach…',
    pt: 'Viajando para…',
    ru: 'Едет в…',
    hi: 'यात्रा कर रहे हैं…',
    ja: '旅行中…',
    zh: '前往…'
  },
  ask: {
    en: 'Where are you traveling to?',
    ar: 'إلى أين تسافر؟',
    es: '¿A dónde viajas?',
    fr: 'Où allez-vous ?',
    de: 'Wohin reist du?',
    pt: 'Para onde você está viajando?',
    ru: 'Куда вы едете?',
    hi: 'आप कहाँ जा रहे हैं?',
    ja: 'どこへ行きますか？',
    zh: '你要去哪里？'
  },
  line: {
    en: ' is {e} traveling to {x}',
    ar: ' {e} مسافر إلى {x}',
    es: ' {e} está viajando a {x}',
    fr: ' {e} part pour {x}',
    de: ' {e} reist nach {x}',
    pt: ' {e} está viajando para {x}',
    ru: ' {e} едет в {x}',
    hi: ' {e} {x} की यात्रा पर हैं',
    ja: ' {e} {x}へ旅行中',
    zh: ' {e} 正在前往{x}'
  }
}, {
  key: 'listening',
  emoji: '🎧',
  icon: 'headphones',
  label: {
    en: 'Listening to…',
    ar: 'يستمع إلى…',
    es: 'Escuchando…',
    fr: 'Écoute…',
    de: 'Hört…',
    pt: 'Ouvindo…',
    ru: 'Слушает…',
    hi: 'सुन रहे हैं…',
    ja: '聴いている…',
    zh: '听…'
  },
  ask: {
    en: 'What are you listening to?',
    ar: 'إلى ماذا تستمع؟',
    es: '¿Qué estás escuchando?',
    fr: 'Qu’écoutez-vous ?',
    de: 'Was hörst du?',
    pt: 'O que você está ouvindo?',
    ru: 'Что вы слушаете?',
    hi: 'आप क्या सुन रहे हैं?',
    ja: '何を聴いていますか？',
    zh: '你在听什么？'
  },
  line: {
    en: ' is {e} listening to {x}',
    ar: ' {e} يستمع إلى {x}',
    es: ' {e} está escuchando {x}',
    fr: ' {e} écoute {x}',
    de: ' {e} hört {x}',
    pt: ' {e} está ouvindo {x}',
    ru: ' {e} слушает {x}',
    hi: ' {e} {x} सुन रहे हैं',
    ja: ' {e} {x}を聴いています',
    zh: ' {e} 正在听{x}'
  }
}, {
  key: 'looking_for',
  emoji: '🔎',
  icon: 'search',
  label: {
    en: 'Looking for…',
    ar: 'يبحث عن…',
    es: 'Buscando…',
    fr: 'Cherche…',
    de: 'Sucht…',
    pt: 'Procurando…',
    ru: 'Ищет…',
    hi: 'ढूंढ रहे हैं…',
    ja: '探している…',
    zh: '寻找…'
  },
  ask: {
    en: 'What are you looking for?',
    ar: 'عمّ تبحث؟',
    es: '¿Qué estás buscando?',
    fr: 'Que cherchez-vous ?',
    de: 'Was suchst du?',
    pt: 'O que você está procurando?',
    ru: 'Что вы ищете?',
    hi: 'आप क्या ढूंढ रहे हैं?',
    ja: '何を探していますか？',
    zh: '你在找什么？'
  },
  line: {
    en: ' is {e} looking for {x}',
    ar: ' {e} يبحث عن {x}',
    es: ' {e} está buscando {x}',
    fr: ' {e} cherche {x}',
    de: ' {e} sucht {x}',
    pt: ' {e} está procurando {x}',
    ru: ' {e} ищет {x}',
    hi: ' {e} {x} ढूंढ रहे हैं',
    ja: ' {e} {x}を探しています',
    zh: ' {e} 正在寻找{x}'
  }
}, {
  key: 'reading',
  emoji: '📖',
  icon: 'book',
  label: {
    en: 'Reading…',
    ar: 'يقرأ…',
    es: 'Leyendo…',
    fr: 'Lit…',
    de: 'Liest…',
    pt: 'Lendo…',
    ru: 'Читает…',
    hi: 'पढ़ रहे हैं…',
    ja: '読書中…',
    zh: '阅读…'
  },
  ask: {
    en: 'What are you reading?',
    ar: 'ماذا تقرأ؟',
    es: '¿Qué estás leyendo?',
    fr: 'Que lisez-vous ?',
    de: 'Was liest du?',
    pt: 'O que você está lendo?',
    ru: 'Что вы читаете?',
    hi: 'आप क्या पढ़ रहे हैं?',
    ja: '何を読んでいますか？',
    zh: '你在读什么？'
  },
  line: {
    en: ' is {e} reading {x}',
    ar: ' {e} يقرأ {x}',
    es: ' {e} está leyendo {x}',
    fr: ' {e} lit {x}',
    de: ' {e} liest {x}',
    pt: ' {e} está lendo {x}',
    ru: ' {e} читает {x}',
    hi: ' {e} {x} पढ़ रहे हैं',
    ja: ' {e} {x}を読んでいます',
    zh: ' {e} 正在读{x}'
  }
}, {
  key: 'playing',
  emoji: '🎮',
  icon: 'game',
  label: {
    en: 'Playing…',
    ar: 'يلعب…',
    es: 'Jugando…',
    fr: 'Joue à…',
    de: 'Spielt…',
    pt: 'Jogando…',
    ru: 'Играет в…',
    hi: 'खेल रहे हैं…',
    ja: 'プレイ中…',
    zh: '玩…'
  },
  ask: {
    en: 'What are you playing?',
    ar: 'ماذا تلعب؟',
    es: '¿A qué estás jugando?',
    fr: 'À quoi jouez-vous ?',
    de: 'Was spielst du?',
    pt: 'O que você está jogando?',
    ru: 'Во что вы играете?',
    hi: 'आप क्या खेल रहे हैं?',
    ja: '何をプレイしていますか？',
    zh: '你在玩什么？'
  },
  line: {
    en: ' is {e} playing {x}',
    ar: ' {e} يلعب {x}',
    es: ' {e} está jugando a {x}',
    fr: ' {e} joue à {x}',
    de: ' {e} spielt {x}',
    pt: ' {e} está jogando {x}',
    ru: ' {e} играет в {x}',
    hi: ' {e} {x} खेल रहे हैं',
    ja: ' {e} {x}をプレイ中',
    zh: ' {e} 正在玩{x}'
  }
}, {
  key: 'supporting',
  emoji: '🙌',
  icon: 'support',
  label: {
    en: 'Supporting…',
    ar: 'يدعم…',
    es: 'Apoyando a…',
    fr: 'Soutient…',
    de: 'Unterstützt…',
    pt: 'Apoiando…',
    ru: 'Поддерживает…',
    hi: 'समर्थन कर रहे हैं…',
    ja: '応援中…',
    zh: '支持…'
  },
  ask: {
    en: 'Who are you supporting?',
    ar: 'من تدعم؟',
    es: '¿A quién apoyas?',
    fr: 'Qui soutenez-vous ?',
    de: 'Wen unterstützt du?',
    pt: 'Quem você está apoiando?',
    ru: 'Кого вы поддерживаете?',
    hi: 'आप किसका समर्थन कर रहे हैं?',
    ja: '誰を応援していますか？',
    zh: '你在支持谁？'
  },
  line: {
    en: ' is {e} supporting {x}',
    ar: ' {e} يدعم {x}',
    es: ' {e} está apoyando a {x}',
    fr: ' {e} soutient {x}',
    de: ' {e} unterstützt {x}',
    pt: ' {e} está apoiando {x}',
    ru: ' {e} поддерживает {x}',
    hi: ' {e} {x} का समर्थन कर रहे हैं',
    ja: ' {e} {x}を応援中',
    zh: ' {e} 正在支持{x}'
  }
}];
const FEELING_LINE = {
  en: ' is {e} feeling {x}',
  ar: ' {e} يشعر بأنه {x}',
  es: ' {e} se siente {x}',
  fr: ' {e} se sent {x}',
  de: ' {e} fühlt sich {x}',
  pt: ' {e} está se sentindo {x}',
  ru: ' {e} настроение: {x}',
  hi: ' {e} {x} महसूस कर रहे हैं',
  ja: ' {e} {x}気分',
  zh: ' {e} 感觉{x}'
};
const FEELING_BY_KEY = FEELINGS.reduce((m, f) => {
  m[f.key] = f;
  return m;
}, {});
const ACTIVITY_BY_KEY = ACTIVITIES.reduce((m, a) => {
  m[a.key] = a;
  return m;
}, {});

/* control characters become spaces — a byline is one line of ordinary text */
const noControls = t => Array.from(t).map(ch => {
  const n = ch.charCodeAt(0);
  return n < 32 || n === 127 ? ' ' : ch;
}).join('');

/** The one shape a feeling may have — on write AND on read. Anything else is null. */
export function cleanFeeling(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const key = String(raw.key || '');
  if (raw.kind === 'feeling') return FEELING_BY_KEY[key] ? {
    kind: 'feeling',
    key
  } : null;
  if (raw.kind === 'activity') {
    if (!ACTIVITY_BY_KEY[key]) return null;
    const text = noControls(String(raw.text == null ? '' : raw.text)).replace(/\s+/g, ' ').trim().slice(0, ACTIVITY_TEXT_MAX).trim();
    return text ? {
      kind: 'activity',
      key,
      text
    } : null;
  }
  return null;
}
export function feelingEmoji(f) {
  const c = cleanFeeling(f);
  if (!c) return '';
  return c.kind === 'feeling' ? FEELING_BY_KEY[c.key].emoji : ACTIVITY_BY_KEY[c.key].emoji;
}
/** The grid / chip word: "happy", "Watching…" */
export const feelingLabel = (key, lang) => pick(FEELING_BY_KEY[key] && FEELING_BY_KEY[key].L, lang, key);
export const activityLabel = (key, lang) => pick(ACTIVITY_BY_KEY[key] && ACTIVITY_BY_KEY[key].label, lang, key);
export const activityAsk = (key, lang) => pick(ACTIVITY_BY_KEY[key] && ACTIVITY_BY_KEY[key].ask, lang, '');

/** " is 😊 feeling happy" — appended to the author's name. '' for no / bad feeling. */
export function feelingSegment(raw, lang) {
  const f = cleanFeeling(raw);
  if (!f) return '';
  if (f.kind === 'feeling') {
    const d = FEELING_BY_KEY[f.key];
    return fill(pick(FEELING_LINE, lang), {
      e: d.emoji,
      x: pick(d.L, lang)
    });
  }
  const a = ACTIVITY_BY_KEY[f.key];
  return fill(pick(a.line, lang), {
    e: a.emoji,
    x: f.text || ''
  });
}

/** The chip in the composer: "😊 feeling happy" (no leading "is"). */
export function feelingChip(raw, lang) {
  return feelingSegment(raw, lang).replace(/^\s*is\s+/, '').trim();
}

/* the place after a feeling loses its own "is": "… feeling happy at 📍 Riyadh" */
const AT_AFTER = {
  en: ' at 📍 ',
  ar: ' في 📍 ',
  es: ' en 📍 ',
  fr: ' à 📍 ',
  de: ' in 📍 ',
  pt: ' em 📍 ',
  ru: ' в 📍 ',
  hi: ' 📍 ',
  ja: ' 📍 ',
  zh: ' 在 📍 '
};
const TO_AFTER = {
  en: ' on the way to ✈️ ',
  ar: ' في الطريق إلى ✈️ ',
  es: ' de camino a ✈️ ',
  fr: ' en route pour ✈️ ',
  de: ' unterwegs nach ✈️ ',
  pt: ' a caminho de ✈️ ',
  ru: ' по пути в ✈️ ',
  hi: ' ✈️ ',
  ja: ' ✈️ ',
  zh: ' 前往 ✈️ '
};
export function placeAfterFeeling(type, lang) {
  return pick(type === 'check-in' ? AT_AFTER : TO_AFTER, lang);
}

/* ═══ THE LINE AS PIECES — minimal icons instead of colour emoji ═══════════════
   An activity and a place are drawn with a small monochrome icon in the text's
   own colour ("is [tv] watching the final at [pin] Riyadh"), not a colour emoji.
   The icon is named by a NEUTRAL key; each client maps the key to its own icon
   set (the phone: Ionicons, the browser: lucide). A feeling keeps its emoji —
   a face is the point of a feeling.
   `feelingSegment` stays the plain-text form (drafts, search, notifications). */

export const PLACE_ICON = {
  'check-in': 'pin',
  traveling: 'plane'
};
const pushText = (out, text) => {
  if (!text) return;
  const last = out[out.length - 1];
  if (last && typeof last.text === 'string') last.text += text;else out.push({
    text
  });
};
/** A string with 📍 / ✈️ in it, with those two drawn as icons. */
export function iconizePlace(str) {
  const out = [];
  const parts = String(str || '').split(/(\u{1F4CD}|\u2708\uFE0F?)/u);
  for (const part of parts) {
    if (part === '\u{1F4CD}') out.push({
      icon: 'pin'
    });else if (part === '\u2708\uFE0F' || part === '\u2708') out.push({
      icon: 'plane'
    });else pushText(out, part);
  }
  return out;
}
/** feelingSegment as pieces: an activity's emoji becomes its icon; a feeling keeps its emoji. */
export function feelingPieces(raw, lang) {
  const f = cleanFeeling(raw);
  if (!f) return [];
  if (f.kind === 'feeling') return [{
    text: feelingSegment(f, lang)
  }];
  const a = ACTIVITY_BY_KEY[f.key];
  const tpl = pick(a.line, lang);
  const out = [];
  const at = tpl.indexOf('{e}');
  if (at < 0) {
    pushText(out, fill(tpl, {
      e: '',
      x: f.text || ''
    }));
    return out;
  }
  pushText(out, fill(tpl.slice(0, at), {
    x: f.text || ''
  }));
  out.push({
    icon: a.icon
  });
  pushText(out, fill(tpl.slice(at + 3), {
    x: f.text || ''
  }));
  return out;
}
/** feelingChip as pieces: "[tv] watching the final" (no leading "is"). */
export function feelingChipPieces(raw, lang) {
  const out = feelingPieces(raw, lang).map(x => x.text != null ? {
    text: x.text
  } : {
    icon: x.icon
  });
  const first = out[0];
  if (first && first.text != null) {
    first.text = first.text.replace(/^\s*is\s+/, '').replace(/^\s+/, '');
    if (!first.text) out.shift();
  }
  const last = out[out.length - 1];
  if (last && last.text != null) {
    last.text = last.text.replace(/\s+$/, '');
    if (!last.text) out.pop();
  }
  return out;
}
/** The joiner before a place that follows a feeling, as pieces ("at [pin] "). */
export function placeAfterFeelingPieces(type, lang) {
  return iconizePlace(placeAfterFeeling(type, lang));
}
/** Which icon an activity is drawn with (the composer's grid). */
export const activityIcon = key => ACTIVITY_BY_KEY[key] ? ACTIVITY_BY_KEY[key].icon : null;

/* ═══ "NEAR DEARBORN, MI" — ONLY WHEN THE POST SAYS NOTHING ABOUT WHERE ════════
   `locationLabel` is the automatic geo-tag stamped at publish. A post that
   already names a place ("is at 📍 Henry Ford Museum", "is traveling to ✈️
   Paris") or says what the person is doing ("is watching the final") has its own
   line; "near Dearborn, MI" under it either repeats it or contradicts it — a
   post about arriving in Paris, tagged near the city it left from. So the auto
   tag shows only on a post with no place, no feeling and no activity. One rule
   for every surface: the feed, the viewer, the reels, the previews, the web. */
export function nearLabelFor(post) {
  const p = post || {};
  const label = typeof p.locationLabel === 'string' ? p.locationLabel.trim() : '';
  if (!label) return null;
  const st = p.status;
  if (st && typeof st === 'object' && (st.type === 'check-in' || st.type === 'traveling')) return null;
  if (cleanFeeling(p.feeling)) return null;
  return label;
}

/** Feelings filtered by what was typed — matches the word in the reader's language AND in English. */
export function searchFeelings(q, lang) {
  const s = fold(q);
  if (!s) return FEELINGS;
  return FEELINGS.filter(f => fold(pick(f.L, lang)).includes(s) || fold(f.L.en).includes(s) || f.key.includes(s));
}
export function searchActivities(q, lang) {
  const s = fold(q);
  if (!s) return ACTIVITIES;
  return ACTIVITIES.filter(a => fold(pick(a.label, lang)).includes(s) || fold(a.label.en).includes(s) || a.key.includes(s));
}
function fold(s) {
  return String(s || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[….]/g, '').replace(/\s+/g, ' ').trim();
}

/* ═══ DRAFTS ════════════════════════════════════════════════════════════════ */

const s = (v, n) => typeof v === 'string' ? v.slice(0, n) : '';
function cleanStatus(x) {
  if (!x || typeof x !== 'object') return null;
  if (x.type === 'check-in' && s(x.location, 120)) return {
    type: 'check-in',
    location: s(x.location, 120)
  };
  if (x.type === 'traveling' && s(x.to, 120)) return {
    type: 'traveling',
    from: s(x.from, 120),
    to: s(x.to, 120)
  };
  return null;
}
export function cleanMusic(x) {
  if (!x || typeof x !== 'object' || !/^https:\/\//i.test(String(x.url || ''))) return null;
  const out = {
    url: s(x.url, 600)
  };
  if (x.title) out.title = s(x.title, 140);
  if (x.artist) out.artist = s(x.artist, 140);
  if (x.license) out.license = s(x.license, 20);
  if (x.page && /^https:\/\//i.test(String(x.page))) out.page = s(x.page, 600);
  if (x.provider) out.provider = s(x.provider, 30);
  if (x.id) out.id = s(String(x.id), 80);
  if (Number(x.startMs) > 0) out.startMs = Math.floor(Number(x.startMs));
  return out;
}

/** A picked catalogue track (services/soundLibrary Sound) → what the composer holds. */
export function musicFromSound(x) {
  if (!x) return null;
  return cleanMusic({
    url: x.url,
    title: x.title,
    artist: x.artist,
    license: x.license && x.license.code,
    page: x.page,
    provider: x.provider,
    id: x.id
  });
}

/** The post's sound fields for a song added in the composer (a post with no video).
    The same fields a video's song writes, so every reader credits it the same way. */
export function musicFields(m) {
  const c = cleanMusic(m);
  if (!c) return null;
  return {
    audioTrackUrl: c.url,
    audioTrackTitle: c.title || null,
    audioTrackArtist: c.artist || null,
    audioTrackLicense: c.license || null,
    audioTrackPage: c.page || null,
    audioTrackProvider: c.provider || null,
    audioTrackId: c.id || null,
    audioStartMs: c.startMs || 0,
    isOriginalMuted: false,
    isAddedSoundMuted: false,
    originalVolume: 100,
    addedSoundVolume: 100
  };
}

/** Does this post carry a song that nothing else plays — no video to follow? */
export function playsOwnMusic(post) {
  if (!post || !post.audioTrackUrl || post.isAddedSoundMuted === true) return false;
  const media = Array.isArray(post.media) ? post.media : [];
  if (media.some(m => m && m.type === 'video')) return false;
  return !(post.mediaType === 'video' || post.videoUrl);
}

/** What a stored draft may be. Older than DRAFT_MAX_AGE_MS, malformed, or empty → null. */
export function cleanDraft(raw, now) {
  if (!raw || typeof raw !== 'object' || raw.v !== 1) return null;
  const at = Number(raw.at) || 0;
  if (!at || now - at > DRAFT_MAX_AGE_MS || at > now + 60 * 1000) return null;
  const media = (Array.isArray(raw.media) ? raw.media : []).filter(m => m && typeof m === 'object' && typeof m.uri === 'string' && m.uri && (m.type === 'image' || m.type === 'video')).slice(0, DRAFT_MEDIA_MAX);
  const poll = raw.poll && Array.isArray(raw.poll.options) ? {
    options: raw.poll.options.filter(o => o && typeof o.id === 'string').slice(0, 10).map(o => ({
      id: s(o.id, 40),
      text: s(o.text, 100)
    })),
    multiple: !!raw.poll.multiple
  } : null;
  const tags = (Array.isArray(raw.tags) ? raw.tags : []).filter(p => p && typeof p.uid === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(p.uid)).slice(0, 20).map(p => ({
    uid: p.uid,
    name: s(p.name, 80) || '…',
    ...(typeof p.image === 'string' && p.image ? {
      image: s(p.image, 600)
    } : {})
  }));
  const d = {
    v: 1,
    at,
    text: s(raw.text, DRAFT_TEXT_MAX),
    media,
    textStyle: raw.textStyle && typeof raw.textStyle === 'object' ? raw.textStyle : null,
    styleOff: !!raw.styleOff,
    poll: poll && poll.options.length >= 2 ? poll : null,
    tags,
    status: cleanStatus(raw.status),
    feeling: cleanFeeling(raw.feeling),
    audience: raw.audience === 'friends' ? 'friends' : 'everyone',
    comments: raw.comments !== false,
    ai: raw.ai === true,
    music: cleanMusic(raw.music),
    pageId: typeof raw.pageId === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(raw.pageId) ? raw.pageId : null
  };
  return draftWorthKeeping(d) ? d : null;
}

/** Is there anything here a person would be sorry to lose? Settings alone are not a post. */
export function draftWorthKeeping(d) {
  if (!d) return false;
  const hasText = !!(d.text && d.text.trim());
  const hasMedia = !!(d.media && d.media.length);
  const hasTags = !!(d.tags && d.tags.length);
  return hasText || hasMedia || hasTags || !!d.poll || !!d.status || !!cleanFeeling(d.feeling) || !!d.music;
}

/** One line for "Your draft": the words, else what it carries. */
export function draftPreview(d, lang) {
  if (!d) return '';
  const t = String(d.text || '').replace(/\s+/g, ' ').trim();
  if (t) return t.slice(0, 120);
  if (d.media && d.media.length) return composeL(d.media.length === 1 ? 'draft_one_item' : 'draft_items', lang).replace('{n}', String(d.media.length));
  if (d.feeling) return feelingChip(d.feeling, lang);
  return composeL('draft', lang);
}

/* ═══ COMPOSER WORDS — ten languages ════════════════════════════════════════ */
const W = {
  create_post: {
    en: 'Create post',
    ar: 'إنشاء منشور',
    es: 'Crear publicación',
    fr: 'Créer une publication',
    de: 'Beitrag erstellen',
    pt: 'Criar publicação',
    ru: 'Создать публикацию',
    hi: 'पोस्ट बनाएँ',
    ja: '投稿を作成',
    zh: '创建帖子'
  },
  new_post: {
    en: 'New post',
    ar: 'منشور جديد',
    es: 'Nueva publicación',
    fr: 'Nouvelle publication',
    de: 'Neuer Beitrag',
    pt: 'Nova publicação',
    ru: 'Новая публикация',
    hi: 'नई पोस्ट',
    ja: '新しい投稿',
    zh: '新帖子'
  },
  next: {
    en: 'Next',
    ar: 'التالي',
    es: 'Siguiente',
    fr: 'Suivant',
    de: 'Weiter',
    pt: 'Avançar',
    ru: 'Далее',
    hi: 'आगे',
    ja: '次へ',
    zh: '下一步'
  },
  post: {
    en: 'Post',
    ar: 'نشر',
    es: 'Publicar',
    fr: 'Publier',
    de: 'Posten',
    pt: 'Publicar',
    ru: 'Опубликовать',
    hi: 'पोस्ट करें',
    ja: '投稿',
    zh: '发布'
  },
  done: {
    en: 'Done',
    ar: 'تم',
    es: 'Listo',
    fr: 'Terminé',
    de: 'Fertig',
    pt: 'Concluir',
    ru: 'Готово',
    hi: 'हो गया',
    ja: '完了',
    zh: '完成'
  },
  add_to_post: {
    en: 'Add to your post',
    ar: 'أضف إلى منشورك',
    es: 'Agregar a tu publicación',
    fr: 'Ajouter à votre publication',
    de: 'Zu deinem Beitrag hinzufügen',
    pt: 'Adicionar à publicação',
    ru: 'Добавить в публикацию',
    hi: 'अपनी पोस्ट में जोड़ें',
    ja: '投稿に追加',
    zh: '添加到帖子'
  },
  photo_video: {
    en: 'Photo/video',
    ar: 'صورة/فيديو',
    es: 'Foto/video',
    fr: 'Photo/vidéo',
    de: 'Foto/Video',
    pt: 'Foto/vídeo',
    ru: 'Фото/видео',
    hi: 'फ़ोटो/वीडियो',
    ja: '写真・動画',
    zh: '照片/视频'
  },
  tag_people: {
    en: 'Tag people',
    ar: 'الإشارة إلى أشخاص',
    es: 'Etiquetar personas',
    fr: 'Identifier des personnes',
    de: 'Personen markieren',
    pt: 'Marcar pessoas',
    ru: 'Отметить людей',
    hi: 'लोगों को टैग करें',
    ja: 'タグ付け',
    zh: '标记好友'
  },
  feeling_activity: {
    en: 'Feeling/activity',
    ar: 'شعور/نشاط',
    es: 'Sentimiento/actividad',
    fr: 'Humeur/activité',
    de: 'Gefühl/Aktivität',
    pt: 'Sentimento/atividade',
    ru: 'Чувство/действие',
    hi: 'भावना/गतिविधि',
    ja: '気分・アクティビティ',
    zh: '感受/活动'
  },
  check_in: {
    en: 'Check in',
    ar: 'تسجيل الوصول',
    es: 'Registrar visita',
    fr: 'Identifier un lieu',
    de: 'Einchecken',
    pt: 'Fazer check-in',
    ru: 'Отметиться',
    hi: 'चेक इन',
    ja: 'チェックイン',
    zh: '签到'
  },
  poll: {
    en: 'Poll',
    ar: 'استطلاع',
    es: 'Encuesta',
    fr: 'Sondage',
    de: 'Umfrage',
    pt: 'Enquete',
    ru: 'Опрос',
    hi: 'पोल',
    ja: 'アンケート',
    zh: '投票'
  },
  background: {
    en: 'Background color',
    ar: 'لون الخلفية',
    es: 'Color de fondo',
    fr: 'Couleur d’arrière-plan',
    de: 'Hintergrundfarbe',
    pt: 'Cor de fundo',
    ru: 'Цвет фона',
    hi: 'पृष्ठभूमि रंग',
    ja: '背景色',
    zh: '背景颜色'
  },
  background_hint: {
    en: 'Backgrounds are for short text posts',
    ar: 'الخلفيات للمنشورات النصية القصيرة',
    es: 'Los fondos son para textos cortos',
    fr: 'Les arrière-plans sont pour les textes courts',
    de: 'Hintergründe gibt es für kurze Textbeiträge',
    pt: 'Fundos são para textos curtos',
    ru: 'Фон доступен для коротких текстов',
    hi: 'पृष्ठभूमि छोटे टेक्स्ट के लिए है',
    ja: '背景は短いテキスト投稿用です',
    zh: '背景仅适用于简短文字帖子'
  },
  post_audience: {
    en: 'Post audience',
    ar: 'جمهور المنشور',
    es: 'Público de la publicación',
    fr: 'Audience de la publication',
    de: 'Publikum des Beitrags',
    pt: 'Público da publicação',
    ru: 'Аудитория публикации',
    hi: 'पोस्ट दर्शक',
    ja: '投稿の公開範囲',
    zh: '帖子受众'
  },
  public: {
    en: 'Public',
    ar: 'عام',
    es: 'Público',
    fr: 'Public',
    de: 'Öffentlich',
    pt: 'Público',
    ru: 'Доступно всем',
    hi: 'सार्वजनिक',
    ja: '公開',
    zh: '公开'
  },
  public_hint: {
    en: 'Anyone on Allo',
    ar: 'أي شخص على Allo',
    es: 'Cualquiera en Allo',
    fr: 'Tout le monde sur Allo',
    de: 'Alle auf Allo',
    pt: 'Qualquer pessoa no Allo',
    ru: 'Все в Allo',
    hi: 'Allo पर कोई भी',
    ja: 'Alloの全員',
    zh: 'Allo 上的任何人'
  },
  friends: {
    en: 'Friends',
    ar: 'الأصدقاء',
    es: 'Amigos',
    fr: 'Amis',
    de: 'Freunde',
    pt: 'Amigos',
    ru: 'Друзья',
    hi: 'मित्र',
    ja: '友達',
    zh: '好友'
  },
  friends_hint: {
    en: 'Your friends on Allo',
    ar: 'أصدقاؤك على Allo',
    es: 'Tus amigos en Allo',
    fr: 'Vos amis sur Allo',
    de: 'Deine Freunde auf Allo',
    pt: 'Seus amigos no Allo',
    ru: 'Ваши друзья в Allo',
    hi: 'Allo पर आपके मित्र',
    ja: 'Alloの友達',
    zh: '你在 Allo 上的好友'
  },
  who_can_see: {
    en: 'Who can see your post?',
    ar: 'من يمكنه رؤية منشورك؟',
    es: '¿Quién puede ver tu publicación?',
    fr: 'Qui peut voir votre publication ?',
    de: 'Wer kann deinen Beitrag sehen?',
    pt: 'Quem pode ver sua publicação?',
    ru: 'Кто может видеть публикацию?',
    hi: 'आपकी पोस्ट कौन देख सकता है?',
    ja: '投稿を見られるのは？',
    zh: '谁可以看到你的帖子？'
  },
  add_music: {
    en: 'Add music',
    ar: 'إضافة موسيقى',
    es: 'Agregar música',
    fr: 'Ajouter de la musique',
    de: 'Musik hinzufügen',
    pt: 'Adicionar música',
    ru: 'Добавить музыку',
    hi: 'संगीत जोड़ें',
    ja: '音楽を追加',
    zh: '添加音乐'
  },
  music: {
    en: 'Music',
    ar: 'موسيقى',
    es: 'Música',
    fr: 'Musique',
    de: 'Musik',
    pt: 'Música',
    ru: 'Музыка',
    hi: 'संगीत',
    ja: '音楽',
    zh: '音乐'
  },
  search_music: {
    en: 'Search music',
    ar: 'ابحث عن موسيقى',
    es: 'Buscar música',
    fr: 'Rechercher de la musique',
    de: 'Musik suchen',
    pt: 'Pesquisar música',
    ru: 'Поиск музыки',
    hi: 'संगीत खोजें',
    ja: '音楽を検索',
    zh: '搜索音乐'
  },
  music_video_note: {
    en: 'This video’s sound is set in the editor',
    ar: 'صوت هذا الفيديو يُضبط من المحرر',
    es: 'El sonido de este video se elige en el editor',
    fr: 'Le son de cette vidéo se règle dans l’éditeur',
    de: 'Der Ton dieses Videos wird im Editor festgelegt',
    pt: 'O som deste vídeo é definido no editor',
    ru: 'Звук этого видео задаётся в редакторе',
    hi: 'इस वीडियो की ध्वनि एडिटर में सेट होती है',
    ja: 'この動画の音は編集画面で設定します',
    zh: '此视频的声音在编辑器中设置'
  },
  remove: {
    en: 'Remove',
    ar: 'إزالة',
    es: 'Quitar',
    fr: 'Retirer',
    de: 'Entfernen',
    pt: 'Remover',
    ru: 'Убрать',
    hi: 'हटाएँ',
    ja: '削除',
    zh: '移除'
  },
  no_results: {
    en: 'No results',
    ar: 'لا توجد نتائج',
    es: 'Sin resultados',
    fr: 'Aucun résultat',
    de: 'Keine Ergebnisse',
    pt: 'Nenhum resultado',
    ru: 'Ничего не найдено',
    hi: 'कोई परिणाम नहीं',
    ja: '結果なし',
    zh: '无结果'
  },
  comments: {
    en: 'Comments',
    ar: 'التعليقات',
    es: 'Comentarios',
    fr: 'Commentaires',
    de: 'Kommentare',
    pt: 'Comentários',
    ru: 'Комментарии',
    hi: 'टिप्पणियाँ',
    ja: 'コメント',
    zh: '评论'
  },
  comments_on: {
    en: 'Anyone who can see the post can comment',
    ar: 'يمكن لكل من يرى المنشور التعليق',
    es: 'Quien vea la publicación puede comentar',
    fr: 'Quiconque voit la publication peut commenter',
    de: 'Wer den Beitrag sieht, kann kommentieren',
    pt: 'Quem vê a publicação pode comentar',
    ru: 'Комментировать может любой, кто видит публикацию',
    hi: 'पोस्ट देखने वाला कोई भी टिप्पणी कर सकता है',
    ja: '投稿を見られる人は誰でもコメントできます',
    zh: '能看到帖子的人都可以评论'
  },
  comments_off: {
    en: 'Comments are turned off',
    ar: 'التعليقات متوقفة',
    es: 'Los comentarios están desactivados',
    fr: 'Les commentaires sont désactivés',
    de: 'Kommentare sind ausgeschaltet',
    pt: 'Os comentários estão desativados',
    ru: 'Комментарии отключены',
    hi: 'टिप्पणियाँ बंद हैं',
    ja: 'コメントはオフです',
    zh: '评论已关闭'
  },
  ai_label: {
    en: 'AI label',
    ar: 'تصنيف الذكاء الاصطناعي',
    es: 'Etiqueta de IA',
    fr: 'Étiquette IA',
    de: 'KI-Kennzeichnung',
    pt: 'Rótulo de IA',
    ru: 'Метка ИИ',
    hi: 'AI लेबल',
    ja: 'AIラベル',
    zh: 'AI 标签'
  },
  ai_label_hint: {
    en: 'Turn this on if your post has a photo or video that was made or edited with AI, so people know. Allo may also add the label when it detects AI on its own.',
    ar: 'فعّل هذا إذا كان منشورك يحتوي على صورة أو فيديو صُنع أو عُدّل بالذكاء الاصطناعي ليعرف الناس ذلك. قد يضيف Allo التصنيف تلقائيًا إذا اكتشفه.',
    es: 'Actívalo si tu publicación tiene una foto o video creado o editado con IA, para que la gente lo sepa. Allo también puede añadir la etiqueta si lo detecta.',
    fr: 'Activez-le si votre publication contient une photo ou une vidéo créée ou retouchée avec l’IA. Allo peut aussi ajouter l’étiquette s’il le détecte.',
    de: 'Schalte das ein, wenn dein Beitrag ein mit KI erstelltes oder bearbeitetes Foto oder Video enthält. Allo kann die Kennzeichnung auch selbst hinzufügen.',
    pt: 'Ative se sua publicação tiver foto ou vídeo criado ou editado com IA, para que as pessoas saibam. O Allo também pode adicionar o rótulo se detectar.',
    ru: 'Включите, если фото или видео в публикации создано или изменено с помощью ИИ. Allo также может добавить метку, если обнаружит это сам.',
    hi: 'यदि आपकी पोस्ट में AI से बनी या संपादित फ़ोटो या वीडियो है तो इसे चालू करें। Allo पहचानने पर लेबल खुद भी जोड़ सकता है।',
    ja: 'AIで作成・編集した写真や動画がある場合はオンにしてください。Alloが検出した場合も自動でラベルを付けることがあります。',
    zh: '如果帖子中的照片或视频由 AI 生成或编辑，请开启此项。Allo 检测到时也可能自动添加标签。'
  },
  post_as: {
    en: 'Post as',
    ar: 'النشر باسم',
    es: 'Publicar como',
    fr: 'Publier en tant que',
    de: 'Posten als',
    pt: 'Publicar como',
    ru: 'Опубликовать от имени',
    hi: 'इस रूप में पोस्ट करें',
    ja: '投稿者',
    zh: '发布身份'
  },
  how_feeling: {
    en: 'How are you feeling?',
    ar: 'بماذا تشعر؟',
    es: '¿Cómo te sientes?',
    fr: 'Comment vous sentez-vous ?',
    de: 'Wie fühlst du dich?',
    pt: 'Como você está se sentindo?',
    ru: 'Как вы себя чувствуете?',
    hi: 'आप कैसा महसूस कर रहे हैं?',
    ja: '今の気分は？',
    zh: '你感觉如何？'
  },
  feelings: {
    en: 'Feelings',
    ar: 'المشاعر',
    es: 'Sentimientos',
    fr: 'Humeurs',
    de: 'Gefühle',
    pt: 'Sentimentos',
    ru: 'Чувства',
    hi: 'भावनाएँ',
    ja: '気分',
    zh: '感受'
  },
  activities: {
    en: 'Activities',
    ar: 'الأنشطة',
    es: 'Actividades',
    fr: 'Activités',
    de: 'Aktivitäten',
    pt: 'Atividades',
    ru: 'Действия',
    hi: 'गतिविधियाँ',
    ja: 'アクティビティ',
    zh: '活动'
  },
  search: {
    en: 'Search',
    ar: 'بحث',
    es: 'Buscar',
    fr: 'Rechercher',
    de: 'Suchen',
    pt: 'Pesquisar',
    ru: 'Поиск',
    hi: 'खोजें',
    ja: '検索',
    zh: '搜索'
  },
  suggestions: {
    en: 'Suggestions',
    ar: 'اقتراحات',
    es: 'Sugerencias',
    fr: 'Suggestions',
    de: 'Vorschläge',
    pt: 'Sugestões',
    ru: 'Рекомендации',
    hi: 'सुझाव',
    ja: 'おすすめ',
    zh: '推荐'
  },
  friend: {
    en: 'Friend',
    ar: 'صديق',
    es: 'Amigo',
    fr: 'Ami',
    de: 'Freund',
    pt: 'Amigo',
    ru: 'Друг',
    hi: 'मित्र',
    ja: '友達',
    zh: '好友'
  },
  profile: {
    en: 'Profile',
    ar: 'ملف شخصي',
    es: 'Perfil',
    fr: 'Profil',
    de: 'Profil',
    pt: 'Perfil',
    ru: 'Профиль',
    hi: 'प्रोफ़ाइल',
    ja: 'プロフィール',
    zh: '个人主页'
  },
  mutual: {
    en: '{n} mutual friends',
    ar: '{n} من الأصدقاء المشتركين',
    es: '{n} amigos en común',
    fr: '{n} amis en commun',
    de: '{n} gemeinsame Freunde',
    pt: '{n} amigos em comum',
    ru: 'Общих друзей: {n}',
    hi: '{n} आपसी मित्र',
    ja: '共通の友達{n}人',
    zh: '{n} 位共同好友'
  },
  mutual_one: {
    en: '1 mutual friend',
    ar: 'صديق مشترك واحد',
    es: '1 amigo en común',
    fr: '1 ami en commun',
    de: '1 gemeinsamer Freund',
    pt: '1 amigo em comum',
    ru: '1 общий друг',
    hi: '1 आपसी मित्र',
    ja: '共通の友達1人',
    zh: '1 位共同好友'
  },
  save_draft: {
    en: 'Save as draft',
    ar: 'حفظ كمسودة',
    es: 'Guardar como borrador',
    fr: 'Enregistrer comme brouillon',
    de: 'Als Entwurf speichern',
    pt: 'Salvar como rascunho',
    ru: 'Сохранить черновик',
    hi: 'ड्राफ़्ट के रूप में सहेजें',
    ja: '下書きとして保存',
    zh: '存为草稿'
  },
  save_draft_hint: {
    en: 'You’ll get it back the next time you create a post.',
    ar: 'ستجده في المرة القادمة التي تنشئ فيها منشورًا.',
    es: 'Lo recuperarás la próxima vez que crees una publicación.',
    fr: 'Vous le retrouverez la prochaine fois que vous créerez une publication.',
    de: 'Du bekommst ihn zurück, wenn du das nächste Mal einen Beitrag erstellst.',
    pt: 'Você o terá de volta na próxima vez que criar uma publicação.',
    ru: 'Он вернётся, когда вы снова создадите публикацию.',
    hi: 'अगली बार पोस्ट बनाते समय यह वापस मिलेगा।',
    ja: '次に投稿を作成するときに戻ります。',
    zh: '下次创建帖子时会恢复。'
  },
  discard: {
    en: 'Discard post',
    ar: 'تجاهل المنشور',
    es: 'Descartar publicación',
    fr: 'Supprimer la publication',
    de: 'Beitrag verwerfen',
    pt: 'Descartar publicação',
    ru: 'Удалить публикацию',
    hi: 'पोस्ट हटाएँ',
    ja: '投稿を破棄',
    zh: '放弃帖子'
  },
  discard_hint: {
    en: 'If you discard now, you’ll lose this post.',
    ar: 'إذا تجاهلته الآن فستفقد هذا المنشور.',
    es: 'Si lo descartas ahora, perderás esta publicación.',
    fr: 'Si vous la supprimez maintenant, elle sera perdue.',
    de: 'Wenn du jetzt verwirfst, geht dieser Beitrag verloren.',
    pt: 'Se descartar agora, você perderá esta publicação.',
    ru: 'Если удалить сейчас, публикация будет потеряна.',
    hi: 'अभी हटाने पर यह पोस्ट खो जाएगी।',
    ja: '今破棄すると、この投稿は失われます。',
    zh: '现在放弃将丢失此帖子。'
  },
  keep_editing: {
    en: 'Continue editing',
    ar: 'متابعة التعديل',
    es: 'Seguir editando',
    fr: 'Continuer la modification',
    de: 'Weiter bearbeiten',
    pt: 'Continuar editando',
    ru: 'Продолжить',
    hi: 'संपादन जारी रखें',
    ja: '編集を続ける',
    zh: '继续编辑'
  },
  leave_q: {
    en: 'Save this post as a draft?',
    ar: 'هل تريد حفظ هذا المنشور كمسودة؟',
    es: '¿Guardar esta publicación como borrador?',
    fr: 'Enregistrer cette publication comme brouillon ?',
    de: 'Diesen Beitrag als Entwurf speichern?',
    pt: 'Salvar esta publicação como rascunho?',
    ru: 'Сохранить как черновик?',
    hi: 'इस पोस्ट को ड्राफ़्ट के रूप में सहेजें?',
    ja: 'この投稿を下書き保存しますか？',
    zh: '将此帖子存为草稿？'
  },
  draft_back: {
    en: 'Your draft is back',
    ar: 'عادت مسودتك',
    es: 'Tu borrador ha vuelto',
    fr: 'Votre brouillon est de retour',
    de: 'Dein Entwurf ist zurück',
    pt: 'Seu rascunho voltou',
    ru: 'Черновик восстановлен',
    hi: 'आपका ड्राफ़्ट वापस आ गया',
    ja: '下書きを復元しました',
    zh: '已恢复草稿'
  },
  draft_saved: {
    en: 'Saved as a draft',
    ar: 'تم الحفظ كمسودة',
    es: 'Guardado como borrador',
    fr: 'Enregistré comme brouillon',
    de: 'Als Entwurf gespeichert',
    pt: 'Salvo como rascunho',
    ru: 'Сохранено в черновик',
    hi: 'ड्राफ़्ट के रूप में सहेजा गया',
    ja: '下書きに保存しました',
    zh: '已存为草稿'
  },
  draft: {
    en: 'Draft',
    ar: 'مسودة',
    es: 'Borrador',
    fr: 'Brouillon',
    de: 'Entwurf',
    pt: 'Rascunho',
    ru: 'Черновик',
    hi: 'ड्राफ़्ट',
    ja: '下書き',
    zh: '草稿'
  },
  draft_one_item: {
    en: '1 photo or video',
    ar: 'صورة أو فيديو واحد',
    es: '1 foto o video',
    fr: '1 photo ou vidéo',
    de: '1 Foto oder Video',
    pt: '1 foto ou vídeo',
    ru: '1 фото или видео',
    hi: '1 फ़ोटो या वीडियो',
    ja: '写真・動画1件',
    zh: '1 张照片或视频'
  },
  draft_items: {
    en: '{n} photos or videos',
    ar: '{n} صور أو فيديوهات',
    es: '{n} fotos o videos',
    fr: '{n} photos ou vidéos',
    de: '{n} Fotos oder Videos',
    pt: '{n} fotos ou vídeos',
    ru: 'Фото и видео: {n}',
    hi: '{n} फ़ोटो या वीडियो',
    ja: '写真・動画{n}件',
    zh: '{n} 张照片或视频'
  },
  on: {
    en: 'On',
    ar: 'تشغيل',
    es: 'Activado',
    fr: 'Activé',
    de: 'An',
    pt: 'Ativado',
    ru: 'Вкл.',
    hi: 'चालू',
    ja: 'オン',
    zh: '开'
  },
  off: {
    en: 'Off',
    ar: 'إيقاف',
    es: 'Desactivado',
    fr: 'Désactivé',
    de: 'Aus',
    pt: 'Desativado',
    ru: 'Выкл.',
    hi: 'बंद',
    ja: 'オフ',
    zh: '关'
  },
  with: {
    en: 'with',
    ar: 'مع',
    es: 'con',
    fr: 'avec',
    de: 'mit',
    pt: 'com',
    ru: 'с',
    hi: 'के साथ',
    ja: 'と一緒に',
    zh: '和'
  },
  you: {
    en: 'You',
    ar: 'أنت',
    es: 'Tú',
    fr: 'Vous',
    de: 'Du',
    pt: 'Você',
    ru: 'Вы',
    hi: 'आप',
    ja: 'あなた',
    zh: '你'
  }
};
export function composeL(key, lang, vars) {
  const v = pick(W[key], lang, key);
  return vars ? fill(v, vars) : v;
}
export function compactCount(n, lang) {
  const x = Math.max(0, Math.floor(Number(n) || 0));
  if (x < 1000) return String(x);
  try {
    return new Intl.NumberFormat(lang2(lang), {
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(x);
  } catch {
    if (x >= 1e6) return `${(x / 1e6).toFixed(x >= 1e7 ? 0 : 1).replace(/\.0$/, '')}M`;
    return `${(x / 1e3).toFixed(x >= 1e4 ? 0 : 1).replace(/\.0$/, '')}K`;
  }
}

/** "Friend · 22 mutual friends" / "Profile · 3 mutual friends" — the second line in the tag picker.
    🟢 Mutual friends only: a count the server works out from friendships it
       already knows (friendGraph), never a stranger's own friend list. */
export function personSubline(p, lang) {
  const m = Math.max(0, Math.floor(Number(p && p.mutual) || 0));
  const head = composeL(p && p.friend ? 'friend' : 'profile', lang);
  if (!m) return head;
  return `${head} · ${m === 1 ? composeL('mutual_one', lang) : composeL('mutual', lang, {
    n: compactCount(m, lang)
  })}`;
}
